import { useEffect, useRef, useState } from "react";

import { useTask } from "../../../hooks/query";
import { useNotification } from "../../../hooks";
import { ImageWithMetadata } from "../../../constants/inspectionPhotos";
import {
    isBlobImageUrl,
    mapUploadResponseToImage,
    toPreviewImage,
} from "../../../utils/taskAssetImages";

interface UseSingleAlbumOptions {
    label: string;
    taskId: string;
    projectId: string;
    images: ImageWithMetadata[];
    onImagesChange: (images: ImageWithMetadata[]) => void;
}

export function useSingleAlbum(props: UseSingleAlbumOptions) {
    const { label, taskId, projectId, images, onImagesChange } = props;
    const { useUploadAsset } = useTask();
    const { mutateAsync: uploadAsset, isPending: isUploading } = useUploadAsset();
    const { showError, showSuccess } = useNotification();
    const imagesRef = useRef(images);
    imagesRef.current = images;

    const [selectedImage, setSelectedImage] = useState<ImageWithMetadata | null>(null);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const cleanupBlobUrls = () => {
        images.forEach((image) => {
            if (isBlobImageUrl(image.url)) {
                URL.revokeObjectURL(image.url);
            }
        });
    };

    useEffect(() => {
        return cleanupBlobUrls;
    }, [images]);

    const uploadFiles = async (files: File[], note = "") => {
        let ok = 0;
        let fail = 0;
        let nextImages = [...imagesRef.current];

        for (const file of files) {
            try {
                const data = new FormData();
                data.append("File", file);
                data.append("Label", label);
                data.append("TaskId", taskId);
                data.append("FileName", file.name);
                data.append("ProjectId", projectId);
                if (note) data.append("Note", note);

                const response = await uploadAsset(data);
                const uploadedImage = mapUploadResponseToImage(response, note);
                const blobIndex = nextImages.findIndex(
                    (img) => isBlobImageUrl(img.url) && !img.assetId,
                );

                if (blobIndex === -1) {
                    nextImages = [...nextImages, uploadedImage];
                } else {
                    URL.revokeObjectURL(nextImages[blobIndex].url);
                    nextImages = [...nextImages];
                    nextImages[blobIndex] = uploadedImage;
                }

                ok++;
            } catch {
                fail++;
            }
        }

        imagesRef.current = nextImages;
        onImagesChange(nextImages);

        if (ok > 0) {
            showSuccess(ok === 1 ? "Image uploaded" : `${ok} images uploaded`);
        }
        if (fail > 0) {
            showError(`Failed to upload ${fail} image(s)`);
        }
    };

    const openFilePicker = () => {
        if (fileInputRef.current) fileInputRef.current.value = "";
        setTimeout(() => fileInputRef.current?.click(), 10);
    };

    const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        if (files.length > 0) {
            setPendingFiles(files);
            setUploadModalOpen(true);
        }
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleUploadConfirm = async (files: File[], note: string) => {
        const previews = files.map((file) => ({
            url: URL.createObjectURL(file),
            note: note || null,
        }));
        const nextImages = [...imagesRef.current, ...previews];
        imagesRef.current = nextImages;
        onImagesChange(nextImages);
        await uploadFiles(files, note);
    };

    const openPreview = (image: ImageWithMetadata | string) => {
        if (typeof image === "string") {
            setSelectedImage({ url: image });
            return;
        }
        setSelectedImage(toPreviewImage(image));
    };

    return {
        selectedImage,
        closePreview: () => setSelectedImage(null),
        openPreview,
        uploadModalOpen,
        setUploadModalOpen,
        pendingFiles,
        setPendingFiles,
        fileInputRef,
        openFilePicker,
        handleFileInput,
        handleUploadConfirm,
        isUploading,
    };
}
